import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API, { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
    Shield, FileText, TrendingUp, BookOpen, Zap, CheckCircle, AlertTriangle,
    Plus, Send, Sparkles, ArrowRight, Target, Clock, ChevronDown, ChevronUp,
    Upload, CreditCard, DollarSign, Search, AlertOctagon, Scale, Lock, Eye,
    ShieldAlert, Award, Flame, Star, Info, Rocket, Play, Calendar,
    AlertCircle, ArrowUp, PartyPopper, RefreshCw, Mail, Timer, X
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   CSS-in-JS Keyframes (injected once)
   ═══════════════════════════════════════════════════════════════ */
const STYLE_ID = 'cr-animations';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
        @keyframes cr-slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes cr-fadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes cr-scaleIn { from { opacity:0; transform:scale(0.8); } to { opacity:1; transform:scale(1); } }
        @keyframes cr-pulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.05); } }
        @keyframes cr-confetti { 0% { opacity:1; transform:translateY(0) rotate(0deg); } 100% { opacity:0; transform:translateY(-120px) rotate(720deg); } }
        @keyframes cr-glow { 0%,100% { box-shadow:0 0 8px rgba(16,185,129,.3); } 50% { box-shadow:0 0 24px rgba(16,185,129,.6); } }
        @keyframes cr-spin { 0% { transform:rotate(0deg); } 100% { transform:rotate(360deg); } }
        @keyframes cr-countUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes cr-shimmer { 0% { background-position:-200% 0; } 100% { background-position:200% 0; } }
        .cr-animate-slide { animation: cr-slideUp 0.5s ease-out both; }
        .cr-animate-fade { animation: cr-fadeIn 0.4s ease-out both; }
        .cr-animate-scale { animation: cr-scaleIn 0.4s ease-out both; }
        .cr-animate-pulse { animation: cr-pulse 2s ease-in-out infinite; }
        .cr-animate-glow { animation: cr-glow 2s ease-in-out infinite; }
        .cr-shimmer { background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%); background-size: 200% 100%; animation: cr-shimmer 1.5s infinite; }
    `;
    document.head.appendChild(style);
}

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════ */
const BUREAUS = ['Experian', 'Equifax', 'TransUnion'];
const PHASES = ['enrolled', 'audit', 'disputes', 'rebuilding', 'graduated'];
const PHASE_LABELS = { enrolled: 'Enrolled', audit: 'Audit', disputes: 'Disputes', rebuilding: 'Rebuilding', graduated: 'Graduated' };
const ITEM_TYPES = ['collection', 'late_payment', 'charge_off', 'bankruptcy', 'foreclosure', 'repossession', 'lien', 'judgment', 'misinformation', 'credit_breach', 'unauthorized_debt_buyer', 'data_breach'];
const ITEM_TYPE_LABELS = {
    collection: 'Collection', late_payment: 'Late Payment', charge_off: 'Charge-Off',
    bankruptcy: 'Bankruptcy', foreclosure: 'Foreclosure', repossession: 'Repossession',
    lien: 'Tax Lien', judgment: 'Judgment', misinformation: 'Misinformation',
    credit_breach: 'Credit Breach', unauthorized_debt_buyer: 'Unauthorized Debt Buyer',
    data_breach: 'Data Breach Victim',
};
const DISPUTE_STATUS_COLORS = {
    draft: 'bg-zinc-100 text-zinc-600', sent: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700', resolved: 'bg-emerald-100 text-emerald-700',
    escalated: 'bg-red-100 text-red-700', removed: 'bg-purple-100 text-purple-700',
};
const URGENCY_COLORS = {
    critical: 'border-red-400 bg-red-50', high: 'border-amber-400 bg-amber-50',
    medium: 'border-blue-400 bg-blue-50', low: 'border-zinc-300 bg-zinc-50', none: 'border-emerald-300 bg-emerald-50',
};
const URGENCY_ICONS = {
    critical: AlertCircle, high: AlertTriangle, medium: Clock, low: Timer, none: CheckCircle,
};

const CREDIT_HACK_INFO = {
    misinformation: {
        title: 'Misinformation Dispute', icon: AlertOctagon, color: 'amber',
        description: 'Information on your credit report that is factually incorrect, outdated, or does not belong to you.',
        examples: ['Wrong balance amount', 'Incorrect account status', 'Wrong date of last activity', 'Account not belonging to you'],
        legal: 'FCRA Section 1681e(b) & 623(a)(1)',
        instructions: ['Review all 3 bureau reports', 'Identify incorrect items with evidence', 'Create dispute below', 'Generate AI letter, send via Certified Mail', 'Bureau has 30 days to investigate'],
    },
    credit_breach: {
        title: 'Credit Breach / Unauthorized Access', icon: ShieldAlert, color: 'red',
        description: 'Your credit file was accessed without a valid permissible purpose.',
        examples: ['Unauthorized hard inquiry', 'Company pulled report without permission', 'Unknown inquiries'],
        legal: 'FCRA Section 604 & 616/617',
        instructions: ['Identify unauthorized inquiries', 'File report at IdentityTheft.gov if needed', 'Place fraud alert with all 3 bureaus', 'Create dispute, generate AI letter', 'Send via Certified Mail'],
    },
    unauthorized_debt_buyer: {
        title: 'Unauthorized Third-Party Debt Buyer', icon: Scale, color: 'purple',
        description: 'A third-party company purchased your alleged debt but lacks proper documentation.',
        examples: ['Debt buyer cannot produce original agreement', 'No valid chain of assignment', 'Debt buyer not licensed in your state'],
        legal: 'FDCPA Section 809(b) & FCRA Section 623',
        instructions: ['Send written validation request within 30 days', 'Request original agreement + chain of title', 'Create dispute below', 'Send via Certified Mail to buyer AND bureau', 'If unvalidated in 30 days, must be removed'],
    },
};


/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */
export default function CreditRepairPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [disputes, setDisputes] = useState([]);
    const [progress, setProgress] = useState([]);
    const [education, setEducation] = useState([]);
    const [actionItems, setActionItems] = useState(null);
    const [scoreData, setScoreData] = useState(null);
    const [goalData, setGoalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('command-center');
    const [timeline, setTimeline] = useState(null);
    const [showCoach, setShowCoach] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [arsenal, setArsenal] = useState(null);

    const loadData = useCallback(async () => {
        try {
            const [profRes, dispRes, progRes, eduRes, actRes, scoreRes, goalRes, tlRes, arsRes, anaRes] = await Promise.all([
                API.get('/credit-repair/profile'),
                API.get('/credit-repair/disputes').catch(() => ({ data: [] })),
                API.get('/credit-repair/progress').catch(() => ({ data: [] })),
                API.get('/credit-repair/education').catch(() => ({ data: [] })),
                API.get('/credit-repair/action-items').catch(() => ({ data: { actions: [], stats: {} } })),
                API.get('/credit-repair/score-history').catch(() => ({ data: { history: [], start_score_range: '', target_score: 640 } })),
                API.post('/credit-repair/check-goal').catch(() => ({ data: { goal_reached: false, affirmation: '', suggestions: [] } })),
                API.get('/credit-repair/timeline').catch(() => ({ data: { events: [], stats: {} } })),
                API.get('/credit-repair/legal-arsenal').catch(() => ({ data: { letters: {}, categories: {} } })),
                API.get('/credit-repair/latest-analysis').catch(() => ({ data: null })),
            ]);
            setProfile(profRes.data);
            setDisputes(dispRes.data);
            setProgress(progRes.data);
            setEducation(eduRes.data);
            setActionItems(actRes.data);
            setScoreData(scoreRes.data);
            setGoalData(goalRes.data);
            setTimeline(tlRes.data);
            setArsenal(arsRes.data);
            if (anaRes.data && !anaRes.data.status) setAnalysis(anaRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) return (
        <div className="min-h-screen bg-[#0a0f1a] flex flex-col items-center justify-center gap-4">
            <div className="w-12 h-12 border-3 border-emerald-500 border-t-transparent rounded-full" style={{ animation: 'cr-spin 0.8s linear infinite' }} />
            <p className="text-emerald-400 text-sm font-medium animate-pulse">Loading your credit repair dashboard...</p>
        </div>
    );

    if (!profile || profile.status === 'not_enrolled') {
        return <EnrollmentView navigate={navigate} />;
    }

    return (
        <div className="min-h-screen bg-[#0a0f1a]" data-testid="credit-repair-dashboard">
            <Navbar variant="dark" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                {/* Header with Score Display */}
                <div className="cr-animate-slide mb-8">
                    <ScoreHero profile={profile} scoreData={scoreData} goalData={goalData} onRefresh={loadData} />
                </div>

                {/* Affirmation Banner */}
                {goalData && (
                    <div className="cr-animate-slide mb-6" style={{ animationDelay: '0.1s' }}>
                        <AffirmationBanner goalData={goalData} />
                    </div>
                )}

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="bg-[#111827] border border-zinc-800 shadow-lg rounded-xl p-1.5 flex-wrap gap-1">
                        <TabsTrigger value="command-center" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400 rounded-lg text-xs sm:text-sm transition-all" data-testid="cr-tab-command-center">
                            <Rocket className="w-4 h-4 mr-1.5" /> Command Center
                        </TabsTrigger>
                        <TabsTrigger value="action-items" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400 rounded-lg text-xs sm:text-sm" data-testid="cr-tab-action-items">
                            <Target className="w-4 h-4 mr-1.5" /> Action Items
                            {actionItems?.stats?.overdue > 0 && <span className="ml-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">{actionItems.stats.overdue}</span>}
                        </TabsTrigger>
                        <TabsTrigger value="disputes" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400 rounded-lg text-xs sm:text-sm" data-testid="cr-tab-disputes">
                            <FileText className="w-4 h-4 mr-1.5" /> Disputes ({disputes.length})
                        </TabsTrigger>
                        <TabsTrigger value="credit-hacks" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400 rounded-lg text-xs sm:text-sm" data-testid="cr-tab-credit-hacks">
                            <Zap className="w-4 h-4 mr-1.5" /> Credit Hacks
                        </TabsTrigger>
                        <TabsTrigger value="legal-arsenal" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400 rounded-lg text-xs sm:text-sm" data-testid="cr-tab-legal-arsenal">
                            <Scale className="w-4 h-4 mr-1.5" /> Legal Arsenal
                        </TabsTrigger>
                        <TabsTrigger value="smart-plan" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400 rounded-lg text-xs sm:text-sm" data-testid="cr-tab-smart-plan">
                            <TrendingUp className="w-4 h-4 mr-1.5" /> Smart Plan
                        </TabsTrigger>
                        <TabsTrigger value="education" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400 rounded-lg text-xs sm:text-sm" data-testid="cr-tab-education">
                            <BookOpen className="w-4 h-4 mr-1.5" /> Education
                        </TabsTrigger>
                        <TabsTrigger value="timeline" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400 rounded-lg text-xs sm:text-sm" data-testid="cr-tab-timeline">
                            <Clock className="w-4 h-4 mr-1.5" /> Journey
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="command-center">
                        <CommandCenter profile={profile} disputes={disputes} onRefresh={loadData} actionItems={actionItems} />
                    </TabsContent>
                    <TabsContent value="action-items">
                        <ActionItemsTab actionItems={actionItems} disputes={disputes} onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="disputes">
                        <DisputesTab disputes={disputes} profile={profile} onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="credit-hacks">
                        <CreditHacksTab onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="legal-arsenal">
                        <LegalArsenalTab arsenal={arsenal} onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="smart-plan">
                        <SmartPlanTab analysis={analysis} onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="education">
                        <EducationTab modules={education} />
                    </TabsContent>
                    <TabsContent value="timeline">
                        <TimelineTab data={timeline} />
                    </TabsContent>
                </Tabs>

                {/* Floating AI Coach */}
                <AICreditCoach show={showCoach} onToggle={() => setShowCoach(!showCoach)} />
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   SCORE HERO — Start vs Current with celebration
   ═══════════════════════════════════════════════════════════════ */
function ScoreHero({ profile, scoreData, goalData, onRefresh }) {
    const [showCelebration, setShowCelebration] = useState(false);
    const [animatedProgress, setAnimatedProgress] = useState(0);
    const target = scoreData?.target_score || 640;
    const startRange = scoreData?.start_score_range || profile.current_score_range || '500-580';
    const currentRange = profile.current_score_range || startRange;

    const parseMid = (range) => {
        if (!range) return 0;
        if (range.includes('-')) { const [l, h] = range.split('-'); return Math.round((parseInt(l) + parseInt(h)) / 2); }
        if (range.endsWith('+')) return parseInt(range);
        return parseInt(range) || 0;
    };
    const startMid = parseMid(startRange);
    const currentMid = parseMid(currentRange);
    const improvement = currentMid - startMid;
    const progressPct = target > startMid ? Math.min(100, Math.round(((currentMid - startMid) / (target - startMid)) * 100)) : 0;

    useEffect(() => {
        let frame = 0;
        const total = 40;
        const interval = setInterval(() => {
            frame++;
            setAnimatedProgress(Math.round((frame / total) * progressPct));
            if (frame >= total) clearInterval(interval);
        }, 25);
        return () => clearInterval(interval);
    }, [progressPct]);

    useEffect(() => {
        if (improvement > 0 && !showCelebration) {
            setTimeout(() => setShowCelebration(true), 800);
        }
    }, [improvement, showCelebration]);

    return (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#111827] to-[#1a2332] border border-zinc-800 p-6 sm:p-8" data-testid="score-hero">
            {/* Celebration confetti */}
            {showCelebration && improvement > 0 && <ConfettiEffect />}

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                {/* Score Cards */}
                <div className="flex items-center gap-4 sm:gap-8">
                    {/* Start Score */}
                    <div className="text-center">
                        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Started At</div>
                        <div className="text-3xl sm:text-4xl font-bold text-zinc-500 font-['Outfit']">{startRange}</div>
                    </div>

                    {/* Arrow with improvement */}
                    <div className="flex flex-col items-center gap-1">
                        <ArrowRight className="w-6 h-6 text-emerald-500" />
                        {improvement > 0 && (
                            <div className="cr-animate-scale flex items-center gap-1 text-emerald-400 text-sm font-bold">
                                <ArrowUp className="w-3 h-3" />+{improvement}
                            </div>
                        )}
                    </div>

                    {/* Current Score */}
                    <div className="text-center">
                        <div className="text-xs text-emerald-400 uppercase tracking-wider mb-1">Current</div>
                        <div className={`text-3xl sm:text-4xl font-bold font-['Outfit'] ${improvement > 0 ? 'text-emerald-400 cr-animate-glow' : 'text-white'}`}
                            style={improvement > 0 ? { borderRadius: '12px', padding: '2px 12px' } : {}}>
                            {currentRange}
                        </div>
                    </div>

                    {/* Target */}
                    <div className="text-center">
                        <div className="text-xs text-amber-400 uppercase tracking-wider mb-1">Target</div>
                        <div className="text-3xl sm:text-4xl font-bold text-amber-400 font-['Outfit']">{target}</div>
                    </div>
                </div>

                {/* Upload + Autopilot */}
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                    <UploadReportButton onUpload={onRefresh} />
                    <AutopilotButton onComplete={onRefresh} />
                </div>
            </div>

            {/* Progress Bar to Target */}
            <div className="mt-6">
                <div className="flex justify-between text-xs mb-2">
                    <span className="text-zinc-500">Progress to Target ({target})</span>
                    <span className="text-emerald-400 font-bold">{animatedProgress}%</span>
                </div>
                <div className="h-3 bg-zinc-800 rounded-full overflow-hidden relative">
                    <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-all duration-1000 relative"
                        style={{ width: `${animatedProgress}%` }}>
                        <div className="absolute inset-0 cr-shimmer rounded-full" />
                    </div>
                </div>
            </div>
        </div>
    );
}

function ConfettiEffect() {
    const colors = ['#10b981', '#f59e0b', '#6366f1', '#ec4899', '#14b8a6', '#f97316'];
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
            {Array.from({ length: 24 }).map((_, i) => (
                <div key={i} className="absolute" style={{
                    left: `${Math.random() * 100}%`, bottom: '0',
                    width: `${6 + Math.random() * 8}px`, height: `${6 + Math.random() * 8}px`,
                    backgroundColor: colors[i % colors.length],
                    borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                    animation: `cr-confetti ${1 + Math.random() * 1.5}s ease-out ${Math.random() * 0.5}s both`,
                }} />
            ))}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   AFFIRMATION BANNER
   ═══════════════════════════════════════════════════════════════ */
function AffirmationBanner({ goalData }) {
    if (!goalData) return null;
    const reached = goalData.goal_reached;
    return (
        <div className={`rounded-xl p-4 border ${reached ? 'bg-emerald-900/40 border-emerald-600' : 'bg-[#111827] border-zinc-800'}`} data-testid="affirmation-banner">
            <div className="flex items-center gap-3">
                {reached ? <Award className="w-6 h-6 text-amber-400 cr-animate-pulse shrink-0" /> : <Sparkles className="w-5 h-5 text-emerald-400 shrink-0" />}
                <div>
                    <p className={`text-sm font-medium ${reached ? 'text-emerald-300' : 'text-zinc-300'}`}>{goalData.affirmation}</p>
                    {goalData.suggestions?.length > 0 && !reached && (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {goalData.suggestions.slice(0, 3).map((s, i) => (
                                <span key={i} className="text-xs bg-zinc-800 text-zinc-400 rounded-full px-3 py-1">{s}</span>
                            ))}
                        </div>
                    )}
                    {reached && goalData.lender_notified && (
                        <p className="text-xs text-amber-400 mt-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Lender has been notified to start your loan process!</p>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   COMMAND CENTER — Autopilot + Quick Stats
   ═══════════════════════════════════════════════════════════════ */
function CommandCenter({ profile, disputes, onRefresh, actionItems }) {
    const stats = actionItems?.stats || {};
    const overdue = actionItems?.actions?.filter(a => a.type === 'overdue') || [];
    const toMail = actionItems?.actions?.filter(a => a.type === 'mail_letter') || [];
    const awaiting = actionItems?.actions?.filter(a => a.type === 'awaiting_response' || a.type === 'deadline_soon') || [];
    const resolved = disputes.filter(d => ['resolved', 'removed'].includes(d.status));

    const cards = [
        { label: 'Total Disputes', value: stats.total || 0, icon: FileText, color: 'text-zinc-300', bg: 'bg-zinc-800/50' },
        { label: 'Ready to Mail', value: toMail.length, icon: Mail, color: 'text-amber-400', bg: 'bg-amber-900/30' },
        { label: 'Awaiting Response', value: awaiting.length, icon: Clock, color: 'text-blue-400', bg: 'bg-blue-900/30' },
        { label: 'Overdue', value: overdue.length, icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-900/30' },
        { label: 'Resolved', value: resolved.length, icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-900/30' },
    ];

    return (
        <div className="space-y-6" data-testid="command-center">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {cards.map((c, i) => {
                    const Icon = c.icon;
                    return (
                        <div key={c.label} className={`${c.bg} rounded-xl border border-zinc-800 p-4 cr-animate-slide`} style={{ animationDelay: `${i * 0.08}s` }}>
                            <Icon className={`w-5 h-5 ${c.color} mb-2`} />
                            <div className={`text-2xl font-bold ${c.color} font-['Outfit']`}>{c.value}</div>
                            <div className="text-xs text-zinc-500">{c.label}</div>
                        </div>
                    );
                })}
            </div>

            {/* Overdue Alerts */}
            {overdue.length > 0 && (
                <div className="bg-red-900/20 border border-red-800 rounded-xl p-5 cr-animate-slide" data-testid="overdue-alerts">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertCircle className="w-5 h-5 text-red-400 cr-animate-pulse" />
                        <h3 className="text-sm font-semibold text-red-300">{overdue.length} Overdue Dispute{overdue.length > 1 ? 's' : ''} - Bureau FCRA Violation</h3>
                    </div>
                    <div className="space-y-2">
                        {overdue.map(a => (
                            <OverdueItem key={a.id} action={a} onRefresh={onRefresh} />
                        ))}
                    </div>
                </div>
            )}

            {/* Ready to Mail */}
            {toMail.length > 0 && (
                <div className="bg-amber-900/20 border border-amber-800 rounded-xl p-5 cr-animate-slide" style={{ animationDelay: '0.15s' }}>
                    <div className="flex items-center gap-2 mb-3">
                        <Mail className="w-5 h-5 text-amber-400" />
                        <h3 className="text-sm font-semibold text-amber-300">{toMail.length} Letter{toMail.length > 1 ? 's' : ''} Ready to Print & Mail</h3>
                    </div>
                    <p className="text-xs text-amber-200/60 mb-3">Print each letter and send via Certified Mail with Return Receipt Requested (USPS Form 3811).</p>
                    <div className="space-y-2">
                        {toMail.slice(0, 8).map(a => (
                            <MailItem key={a.id} action={a} onRefresh={onRefresh} />
                        ))}
                    </div>
                </div>
            )}

            {/* Pipeline Progress */}
            <div className="bg-[#111827] border border-zinc-800 rounded-xl p-5 cr-animate-slide" style={{ animationDelay: '0.2s' }}>
                <h3 className="text-sm font-semibold text-zinc-300 mb-4">Dispute Pipeline</h3>
                <PipelineBar disputes={disputes} />
            </div>

            {/* Negative Items Summary */}
            {profile.negative_items?.length > 0 && (
                <div className="bg-[#111827] border border-zinc-800 rounded-xl p-5 cr-animate-slide" style={{ animationDelay: '0.25s' }}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-zinc-300">Negative Items on File</h3>
                        <Badge className="bg-red-900/50 text-red-400 border-red-800">{profile.negative_items.length} items</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {profile.negative_items.slice(0, 8).map((item, i) => (
                            <span key={item.id || i} className="text-xs bg-zinc-800 text-zinc-400 rounded-full px-3 py-1.5 border border-zinc-700">
                                {item.creditor_name} <span className="text-zinc-600">({item.bureau})</span> {item.amount > 0 && <span className="text-red-400 font-medium ml-1">${item.amount.toLocaleString()}</span>}
                            </span>
                        ))}
                        {profile.negative_items.length > 8 && <span className="text-xs text-zinc-600 self-center">+{profile.negative_items.length - 8} more</span>}
                    </div>
                </div>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   PIPELINE BAR
   ═══════════════════════════════════════════════════════════════ */
function PipelineBar({ disputes }) {
    const stages = [
        { key: 'draft', label: 'Draft', color: 'bg-zinc-600', count: disputes.filter(d => d.status === 'draft').length },
        { key: 'sent', label: 'Mailed', color: 'bg-blue-500', count: disputes.filter(d => d.status === 'sent').length },
        { key: 'in_progress', label: 'Pending', color: 'bg-amber-500', count: disputes.filter(d => d.status === 'in_progress').length },
        { key: 'escalated', label: 'Escalated', color: 'bg-red-500', count: disputes.filter(d => d.status === 'escalated').length },
        { key: 'resolved', label: 'Resolved', color: 'bg-emerald-500', count: disputes.filter(d => ['resolved', 'removed'].includes(d.status)).length },
    ];
    const total = disputes.length || 1;

    return (
        <div>
            <div className="h-4 rounded-full overflow-hidden flex bg-zinc-800">
                {stages.map(s => s.count > 0 && (
                    <div key={s.key} className={`${s.color} h-full transition-all duration-1000`}
                        style={{ width: `${(s.count / total) * 100}%` }} title={`${s.label}: ${s.count}`} />
                ))}
            </div>
            <div className="flex justify-between mt-2 flex-wrap gap-2">
                {stages.map(s => (
                    <div key={s.key} className="flex items-center gap-1.5 text-xs text-zinc-500">
                        <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                        {s.label}: <span className="font-medium text-zinc-300">{s.count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   OVERDUE ITEM — with escalation button
   ═══════════════════════════════════════════════════════════════ */
function OverdueItem({ action, onRefresh }) {
    const [escalating, setEscalating] = useState(false);
    const handleEscalate = async () => {
        setEscalating(true);
        try {
            await API.post(`/credit-repair/disputes/${action.id}/escalate`);
            toast.success('Escalation letter generated! This cites the FCRA violation for non-response.');
            onRefresh();
        } catch (err) { toast.error('Failed to generate escalation letter'); }
        finally { setEscalating(false); }
    };
    return (
        <div className="flex items-center justify-between bg-red-900/30 rounded-lg p-3 border border-red-800/50">
            <div>
                <div className="text-sm font-medium text-red-300">{action.title}</div>
                <div className="text-xs text-red-400/70">{action.description}</div>
            </div>
            <Button size="sm" onClick={handleEscalate} disabled={escalating}
                className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg shrink-0" data-testid={`escalate-${action.id}`}>
                {escalating ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                {escalating ? 'Generating...' : 'Escalation Letter'}
            </Button>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   MAIL ITEM — with date picker to log mailed
   ═══════════════════════════════════════════════════════════════ */
function MailItem({ action, onRefresh }) {
    const [showDate, setShowDate] = useState(false);
    const [mailedDate, setMailedDate] = useState(new Date().toISOString().split('T')[0]);
    const [logging, setLogging] = useState(false);

    const logMailed = async () => {
        setLogging(true);
        try {
            await API.put(`/credit-repair/disputes/${action.id}/log-mailed`, { mailed_date: mailedDate });
            toast.success('Mailed date logged! 30-day countdown started.');
            onRefresh();
        } catch (err) { toast.error('Failed to log mailed date'); }
        finally { setLogging(false); setShowDate(false); }
    };

    return (
        <div className="bg-amber-900/20 rounded-lg p-3 border border-amber-800/50">
            <div className="flex items-center justify-between">
                <div className="text-sm text-amber-300">{action.title}</div>
                <Button size="sm" variant="outline" onClick={() => setShowDate(!showDate)}
                    className="border-amber-700 text-amber-400 hover:bg-amber-900/50 text-xs h-7" data-testid={`log-mailed-${action.id}`}>
                    <Calendar className="w-3 h-3 mr-1" /> Log Mailed
                </Button>
            </div>
            {showDate && (
                <div className="mt-2 flex items-center gap-2 cr-animate-slide">
                    <Input type="date" value={mailedDate} onChange={e => setMailedDate(e.target.value)}
                        className="bg-zinc-900 border-zinc-700 text-zinc-300 h-8 text-xs w-40" data-testid={`mailed-date-${action.id}`} />
                    <Button size="sm" onClick={logMailed} disabled={logging}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8">
                        {logging ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Confirm'}
                    </Button>
                </div>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   ACTION ITEMS TAB — Clean priority list
   ═══════════════════════════════════════════════════════════════ */
function ActionItemsTab({ actionItems, disputes, onRefresh }) {
    if (!actionItems) return null;
    const { actions, stats } = actionItems;

    return (
        <div className="space-y-4" data-testid="action-items-tab">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-zinc-200 font-['Outfit']">Your Action Checklist</h3>
                <Button size="sm" variant="outline" onClick={onRefresh} className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-xs">
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                </Button>
            </div>

            {actions.length === 0 ? (
                <div className="bg-emerald-900/20 border border-emerald-800 rounded-xl p-8 text-center cr-animate-scale">
                    <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                    <div className="text-emerald-300 font-medium">All caught up!</div>
                    <div className="text-xs text-emerald-400/60 mt-1">No pending actions. Upload a new credit report to check progress.</div>
                </div>
            ) : (
                <div className="space-y-2">
                    {actions.map((a, i) => {
                        const Icon = URGENCY_ICONS[a.urgency] || Clock;
                        return (
                            <div key={`${a.id}-${a.type}`}
                                className={`rounded-lg border-l-4 ${URGENCY_COLORS[a.urgency]} p-4 cr-animate-slide`}
                                style={{ animationDelay: `${i * 0.05}s` }}
                                data-testid={`action-item-${a.id}`}>
                                <div className="flex items-start gap-3">
                                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${a.urgency === 'critical' ? 'text-red-500 cr-animate-pulse' : a.urgency === 'high' ? 'text-amber-500' : a.urgency === 'medium' ? 'text-blue-500' : 'text-zinc-400'}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-zinc-900">{a.title}</div>
                                        <div className="text-xs text-zinc-500 mt-0.5">{a.description}</div>
                                        {a.days_left != null && (
                                            <div className="mt-1.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                                                        <div className={`h-full rounded-full transition-all duration-500 ${a.days_left <= 7 ? 'bg-amber-500' : 'bg-blue-500'}`}
                                                            style={{ width: `${Math.max(5, ((30 - (a.days_left || 0)) / 30) * 100)}%` }} />
                                                    </div>
                                                    <span className="text-xs font-medium text-zinc-500">{a.days_left}d left</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <Badge className={`text-[10px] shrink-0 ${a.urgency === 'critical' ? 'bg-red-100 text-red-700' : a.urgency === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'}`}>
                                        {a.urgency}
                                    </Badge>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   AUTOPILOT BUTTON
   ═══════════════════════════════════════════════════════════════ */
function AutopilotButton({ onComplete }) {
    const [running, setRunning] = useState(false);
    const [step, setStep] = useState('');
    const [result, setResult] = useState(null);

    const runAutopilot = async () => {
        setRunning(true);
        setStep('Scanning for data breaches...');
        try {
            const { data } = await API.post('/credit-repair/autopilot');
            setResult(data);
            const s = data.summary;
            toast.success(`Autopilot complete! ${s.disputes_created} disputes created, ${s.letters_generated} letters generated.`);
            onComplete();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Autopilot failed');
        }
        finally { setRunning(false); setStep(''); }
    };

    return (
        <div data-testid="autopilot-section">
            <Button onClick={runAutopilot} disabled={running}
                className={`w-full sm:w-auto rounded-xl text-sm font-semibold transition-all duration-300 ${running ? 'bg-emerald-700 text-emerald-200' : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg shadow-emerald-900/30 hover:shadow-emerald-900/50'}`}
                data-testid="autopilot-btn">
                {running ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> {step}</>
                ) : (
                    <><Rocket className="w-4 h-4 mr-2" /> Run Full Autopilot</>
                )}
            </Button>
            {result && (
                <div className="mt-2 text-xs text-emerald-400/80 cr-animate-fade" data-testid="autopilot-result">
                    {result.summary.breaches_found > 0 && <span>{result.summary.breaches_found} breaches found. </span>}
                    {result.summary.disputes_created} disputes created, {result.summary.letters_generated} letters generated.
                    {result.summary.letters_remaining > 0 && <span className="text-amber-400"> {result.summary.letters_remaining} letters pending — use "Generate All" on Disputes tab.</span>}
                </div>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   UPLOAD REPORT BUTTON
   ═══════════════════════════════════════════════════════════════ */
function UploadReportButton({ onUpload }) {
    const [uploading, setUploading] = useState(false);
    const [compareResult, setCompareResult] = useState(null);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Please upload a PDF file'); return; }
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const { data } = await API.post('/credit-repair/upload-report', formData);
            const itemCount = data.parsed?.negative_items?.length || 0;
            toast.success(`Report analyzed! ${itemCount} items found, ${data.new_items_added || 0} new added.`);

            // Auto-compare with previous report
            try {
                const { data: cmp } = await API.post('/credit-repair/compare-reports');
                if (cmp.status !== 'need_more_reports') {
                    setCompareResult(cmp);
                    if (cmp.items_removed?.length > 0) {
                        toast.success(`${cmp.items_removed.length} items REMOVED from your report! Score ${cmp.score_delta >= 0 ? '+' : ''}${cmp.score_delta} points!`, { duration: 6000 });
                    }
                    if (cmp.items_new?.length > 0) {
                        toast.warning(`${cmp.items_new.length} NEW items detected — auto-disputes created.`);
                    }
                }
            } catch { /* no previous report to compare */ }

            onUpload();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        }
        finally { setUploading(false); e.target.value = ''; }
    };

    return (
        <div data-testid="upload-report-section">
            <input type="file" accept=".pdf" onChange={handleUpload} className="hidden" id="credit-report-upload" data-testid="upload-report-input" />
            <label htmlFor="credit-report-upload">
                <Button asChild disabled={uploading} variant="outline"
                    className="w-full sm:w-auto border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-xl cursor-pointer text-sm">
                    <span>
                        {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        {uploading ? 'Analyzing Report...' : 'Upload Credit Report'}
                    </span>
                </Button>
            </label>
            {compareResult && (
                <CompareResultCard result={compareResult} onDismiss={() => setCompareResult(null)} />
            )}
        </div>
    );
}

function CompareResultCard({ result, onDismiss }) {
    const delta = result.score_delta;
    const positive = delta > 0;
    return (
        <div className={`mt-3 rounded-xl border p-4 cr-animate-scale ${positive ? 'bg-emerald-900/30 border-emerald-700' : delta < 0 ? 'bg-red-900/30 border-red-700' : 'bg-zinc-800 border-zinc-700'}`}
            data-testid="compare-result">
            {positive && <ConfettiEffect />}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    {positive ? <TrendingUp className="w-5 h-5 text-emerald-400" /> : <TrendingUp className="w-5 h-5 text-zinc-400" />}
                    <span className={`text-sm font-semibold ${positive ? 'text-emerald-300' : 'text-zinc-300'}`}>
                        Report Comparison
                    </span>
                </div>
                <button onClick={onDismiss} className="text-zinc-600 hover:text-zinc-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center mb-2">
                <div className="bg-black/20 rounded-lg p-2">
                    <div className={`text-lg font-bold ${positive ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-zinc-300'}`}>
                        {delta >= 0 ? '+' : ''}{delta}
                    </div>
                    <div className="text-[10px] text-zinc-500">Score Change</div>
                </div>
                <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-lg font-bold text-emerald-400">{result.items_removed?.length || 0}</div>
                    <div className="text-[10px] text-zinc-500">Removed</div>
                </div>
                <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-lg font-bold text-amber-400">{result.items_new?.length || 0}</div>
                    <div className="text-[10px] text-zinc-500">New Items</div>
                </div>
            </div>
            {result.items_removed?.length > 0 && (
                <div className="text-xs text-emerald-400/80">
                    Removed: {result.items_removed.map(r => r.creditor_name).join(', ')}
                </div>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   DISPUTES TAB
   ═══════════════════════════════════════════════════════════════ */
function DisputesTab({ disputes, profile, onRefresh }) {
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ bureau: 'Experian', creditor_name: '', account_number: '', item_type: 'collection', amount: '', reason: 'Inaccurate reporting' });
    const [error, setError] = useState('');
    const [generating, setGenerating] = useState(null);
    const [expandedLetter, setExpandedLetter] = useState(null);
    const [bulkGenerating, setBulkGenerating] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [escalating, setEscalating] = useState(null);
    const [cfpbGen, setCfpbGen] = useState(null);

    const addDispute = async () => {
        setError('');
        try {
            await API.post('/credit-repair/disputes', { ...form, amount: parseFloat(form.amount || '0') });
            setShowAdd(false);
            setForm({ bureau: 'Experian', creditor_name: '', account_number: '', item_type: 'collection', amount: '', reason: 'Inaccurate reporting' });
            toast.success('Dispute created');
            onRefresh();
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    };

    const generateLetter = async (disputeId) => {
        setGenerating(disputeId);
        try {
            await API.post(`/credit-repair/disputes/${disputeId}/generate-letter`);
            toast.success('Letter generated!');
            onRefresh();
        } catch (err) { toast.error(err.response?.data?.detail || 'Letter generation failed'); }
        finally { setGenerating(null); }
    };

    const generateAllDraftLetters = async () => {
        const drafts = disputes.filter(d => d.status === 'draft' && !d.dispute_letter);
        if (!drafts.length) { toast.info('No draft disputes need letters.'); return; }
        setBulkGenerating(true);
        let success = 0, failed = 0;
        for (const d of drafts) {
            try { await API.post(`/credit-repair/disputes/${d.id}/generate-letter`); success++; } catch { failed++; }
        }
        toast.success(`Generated ${success} letter${success !== 1 ? 's' : ''}${failed ? `, ${failed} failed` : ''}`);
        setBulkGenerating(false);
        onRefresh();
    };

    const updateStatus = async (disputeId, status) => {
        try {
            await API.put(`/credit-repair/disputes/${disputeId}`, { status });
            toast.success(`Status updated to ${status.replace('_', ' ')}`);
            onRefresh();
        } catch { toast.error('Failed to update'); }
    };

    const downloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const resp = await API.get('/credit-repair/letters-pdf', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([resp.data]));
            const a = document.createElement('a'); a.href = url; a.download = `visionaire_dispute_letters.pdf`; a.click();
            window.URL.revokeObjectURL(url);
            toast.success('PDF bundle downloaded!');
        } catch (err) { toast.error(err.response?.data?.detail || 'No letters ready to download'); }
        finally { setDownloadingPdf(false); }
    };

    const roundEscalate = async (disputeId) => {
        setEscalating(disputeId);
        try {
            const { data } = await API.post(`/credit-repair/disputes/${disputeId}/round-escalate`);
            toast.success(`Round ${data.round} escalation created with ${data.letter_generated ? 'AI letter' : 'pending letter'}!`);
            onRefresh();
        } catch (err) { toast.error('Escalation failed'); }
        finally { setEscalating(null); }
    };

    const generateCfpb = async (disputeId) => {
        setCfpbGen(disputeId);
        try {
            const { data } = await API.post(`/credit-repair/disputes/${disputeId}/cfpb-complaint`);
            toast.success('CFPB complaint generated! Review and submit at consumerfinance.gov');
            onRefresh();
        } catch (err) { toast.error('CFPB complaint generation failed'); }
        finally { setCfpbGen(null); }
    };

    const byBureau = BUREAUS.map(b => ({ bureau: b, items: disputes.filter(d => d.bureau === b) }));
    const draftsNeedLetters = disputes.filter(d => d.status === 'draft' && !d.dispute_letter).length;

    return (
        <div className="space-y-5" data-testid="disputes-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-lg font-semibold text-zinc-200 font-['Outfit']">Dispute Management</h3>
                <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={downloadPdf} disabled={downloadingPdf}
                        data-testid="download-pdf-btn"
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs">
                        {downloadingPdf ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                        {downloadingPdf ? 'Downloading...' : 'Print All Letters (PDF)'}
                    </Button>
                    {draftsNeedLetters > 0 && (
                        <Button size="sm" onClick={generateAllDraftLetters} disabled={bulkGenerating}
                            data-testid="generate-all-letters-btn"
                            className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs">
                            {bulkGenerating ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                            {bulkGenerating ? 'Generating...' : `Generate All (${draftsNeedLetters})`}
                        </Button>
                    )}
                    <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-dispute-btn"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs">
                        <Plus className="w-3 h-3 mr-1" /> New Dispute
                    </Button>
                </div>
            </div>

            {showAdd && (
                <div className="bg-[#111827] rounded-xl border border-zinc-800 p-5 cr-animate-slide" data-testid="add-dispute-form">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-medium text-zinc-300">Create New Dispute</h4>
                        <button onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
                    </div>
                    {error && <div className="text-red-400 text-xs mb-3">{error}</div>}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                            <Label className="text-xs text-zinc-500">Bureau</Label>
                            <select value={form.bureau} onChange={e => setForm(f => ({ ...f, bureau: e.target.value }))}
                                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300" data-testid="dispute-bureau-select">
                                {BUREAUS.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-500">Type</Label>
                            <select value={form.item_type} onChange={e => setForm(f => ({ ...f, item_type: e.target.value }))}
                                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300" data-testid="dispute-type-select">
                                {ITEM_TYPES.map(t => <option key={t} value={t}>{ITEM_TYPE_LABELS[t] || t}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-500">Creditor</Label>
                            <Input value={form.creditor_name} onChange={e => setForm(f => ({ ...f, creditor_name: e.target.value }))}
                                placeholder="Capital One" className="bg-zinc-900 border-zinc-700 text-zinc-300 h-9 text-sm" data-testid="dispute-creditor-input" />
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-500">Amount ($)</Label>
                            <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                                placeholder="0" className="bg-zinc-900 border-zinc-700 text-zinc-300 h-9 text-sm" data-testid="dispute-amount-input" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs text-zinc-500">Reason</Label>
                            <Textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                                className="bg-zinc-900 border-zinc-700 text-zinc-300 text-sm min-h-16" data-testid="dispute-reason-input" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={addDispute} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="submit-dispute-btn">
                            <Plus className="w-3 h-3 mr-1" /> Create
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-zinc-400 text-xs">Cancel</Button>
                    </div>
                </div>
            )}

            {/* Mailing instructions */}
            <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-4">
                <div className="flex items-start gap-2">
                    <Send className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                    <div className="text-xs text-blue-300">
                        <strong>How to Send:</strong> Print the letter, send via <strong>Certified Mail with Return Receipt</strong> (USPS Form 3811). Include copies of your ID. Bureau has 30 days to investigate.
                    </div>
                </div>
            </div>

            {byBureau.map(({ bureau, items }) => (
                <div key={bureau} className="bg-[#111827] rounded-xl border border-zinc-800 p-5 cr-animate-slide">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-semibold text-zinc-200 font-['Outfit']">{bureau}</h4>
                        <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700">{items.length} items</Badge>
                    </div>
                    {items.length === 0 ? (
                        <p className="text-sm text-zinc-600 text-center py-3">No disputes for {bureau}</p>
                    ) : (
                        <div className="space-y-3">
                            {items.map((d, idx) => (
                                <div key={d.id} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 hover:border-zinc-700 transition-all cr-animate-slide"
                                    style={{ animationDelay: `${idx * 0.04}s` }}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <span className="text-sm font-medium text-zinc-200">{d.creditor_name}</span>
                                            <span className="text-xs text-zinc-500 ml-2">
                                                {ITEM_TYPE_LABELS[d.item_type] || d.item_type?.replace('_', ' ')} {d.amount > 0 && `| $${(d.amount || 0).toLocaleString()}`}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge className={DISPUTE_STATUS_COLORS[d.status] || 'bg-zinc-100 text-zinc-600'}>{d.status?.replace('_', ' ')}</Badge>
                                            <span className="text-xs text-zinc-600">R{d.round_number}</span>
                                        </div>
                                    </div>
                                    {d.mailed_date && (
                                        <div className="flex items-center gap-2 mb-2 text-xs">
                                            <Mail className="w-3 h-3 text-blue-400" />
                                            <span className="text-blue-300">Mailed: {new Date(d.mailed_date).toLocaleDateString()}</span>
                                            {d.response_deadline && (
                                                <span className="text-amber-400">| Deadline: {new Date(d.response_deadline).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    )}
                                    <p className="text-xs text-zinc-500 mb-2 line-clamp-2">{d.reason}</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {d.status === 'draft' && !d.dispute_letter && (
                                            <Button size="sm" variant="outline" onClick={() => generateLetter(d.id)} disabled={generating === d.id}
                                                className="text-xs border-zinc-700 text-zinc-400 hover:text-white h-7" data-testid={`gen-letter-${d.id}`}>
                                                {generating === d.id ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                                                {generating === d.id ? 'Generating...' : 'AI Letter'}
                                            </Button>
                                        )}
                                        {d.status === 'draft' && d.dispute_letter && (
                                            <MailDateLogger disputeId={d.id} onRefresh={onRefresh} />
                                        )}
                                        {d.status === 'sent' && (
                                            <Button size="sm" variant="outline" onClick={() => updateStatus(d.id, 'in_progress')}
                                                className="text-xs border-amber-700 text-amber-400 h-7">
                                                <Clock className="w-3 h-3 mr-1" /> Mark In Progress
                                            </Button>
                                        )}
                                        {d.status === 'in_progress' && (
                                            <>
                                                <Button size="sm" variant="outline" onClick={() => updateStatus(d.id, 'resolved')} className="text-xs border-emerald-700 text-emerald-400 h-7">
                                                    <CheckCircle className="w-3 h-3 mr-1" /> Resolved
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => updateStatus(d.id, 'removed')} className="text-xs border-purple-700 text-purple-400 h-7">Removed</Button>
                                                <Button size="sm" variant="outline" onClick={() => roundEscalate(d.id)} disabled={escalating === d.id}
                                                    className="text-xs border-red-700 text-red-400 h-7" data-testid={`escalate-round-${d.id}`}>
                                                    {escalating === d.id ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <ArrowUp className="w-3 h-3 mr-1" />}
                                                    {escalating === d.id ? 'Escalating...' : 'Bureau Verified → Round Up'}
                                                </Button>
                                            </>
                                        )}
                                        {d.status === 'escalated' && (
                                            <Button size="sm" variant="outline" onClick={() => generateCfpb(d.id)} disabled={cfpbGen === d.id}
                                                className="text-xs border-amber-700 text-amber-400 h-7" data-testid={`cfpb-${d.id}`}>
                                                {cfpbGen === d.id ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <AlertOctagon className="w-3 h-3 mr-1" />}
                                                {cfpbGen === d.id ? 'Generating...' : 'CFPB Complaint'}
                                            </Button>
                                        )}
                                        {d.dispute_letter && (
                                            <Button size="sm" variant="ghost" onClick={() => setExpandedLetter(expandedLetter === d.id ? null : d.id)}
                                                className="text-xs text-zinc-500 h-7">
                                                {expandedLetter === d.id ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />} View Letter
                                            </Button>
                                        )}
                                    </div>
                                    {expandedLetter === d.id && d.dispute_letter && (
                                        <div className="mt-3 p-3 bg-zinc-800 rounded-lg text-xs text-zinc-400 whitespace-pre-wrap max-h-64 overflow-y-auto font-mono border border-zinc-700 cr-animate-slide">
                                            {d.dispute_letter}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function MailDateLogger({ disputeId, onRefresh }) {
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [logging, setLogging] = useState(false);
    const log = async () => {
        setLogging(true);
        try {
            await API.put(`/credit-repair/disputes/${disputeId}/log-mailed`, { mailed_date: date });
            toast.success('Mailed! 30-day countdown started.');
            onRefresh();
        } catch { toast.error('Failed'); }
        finally { setLogging(false); setOpen(false); }
    };
    return (
        <div className="flex items-center gap-1">
            {!open ? (
                <Button size="sm" variant="outline" onClick={() => setOpen(true)}
                    className="text-xs border-blue-700 text-blue-400 h-7" data-testid={`mail-btn-${disputeId}`}>
                    <Mail className="w-3 h-3 mr-1" /> Log Mailed
                </Button>
            ) : (
                <div className="flex items-center gap-1 cr-animate-fade">
                    <Input type="date" value={date} onChange={e => setDate(e.target.value)}
                        className="bg-zinc-900 border-zinc-700 text-zinc-300 h-7 text-xs w-32" />
                    <Button size="sm" onClick={log} disabled={logging} className="bg-emerald-600 text-white text-xs h-7">
                        {logging ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'OK'}
                    </Button>
                    <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-300"><X className="w-3 h-3" /></button>
                </div>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   CREDIT HACKS TAB
   ═══════════════════════════════════════════════════════════════ */
function CreditHacksTab({ onRefresh }) {
    const [selectedHack, setSelectedHack] = useState(null);
    const [showDisputeForm, setShowDisputeForm] = useState(false);
    const [form, setForm] = useState({ bureau: 'Experian', creditor_name: '', account_number: '', amount: '', reason: '' });
    const [error, setError] = useState('');

    const createHackDispute = async () => {
        setError('');
        try {
            await API.post('/credit-repair/disputes', {
                bureau: form.bureau, creditor_name: form.creditor_name,
                account_number: form.account_number || '', item_type: selectedHack,
                amount: parseFloat(form.amount || '0'),
                reason: form.reason || CREDIT_HACK_INFO[selectedHack].description,
            });
            toast.success('Dispute created! Go to Disputes tab to generate AI letter.');
            setShowDisputeForm(false);
            onRefresh();
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-5" data-testid="credit-hacks-tab">
            <h3 className="text-lg font-semibold text-zinc-200 font-['Outfit']">Advanced Credit Hacks</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(CREDIT_HACK_INFO).map(([key, hack]) => {
                    const Icon = hack.icon;
                    const isSelected = selectedHack === key;
                    return (
                        <div key={key} onClick={() => { setSelectedHack(isSelected ? null : key); setShowDisputeForm(false); }}
                            className={`cursor-pointer rounded-xl border p-5 transition-all duration-300 ${isSelected ? `border-${hack.color}-600 bg-${hack.color}-900/20` : 'border-zinc-800 bg-[#111827] hover:border-zinc-700'}`}
                            data-testid={`hack-card-${key}`}>
                            <Icon className={`w-6 h-6 text-${hack.color}-400 mb-3`} />
                            <h4 className="text-sm font-semibold text-zinc-200 mb-1">{hack.title}</h4>
                            <p className="text-xs text-zinc-500">{hack.description}</p>
                            <div className="mt-3 text-xs text-zinc-600">{hack.legal}</div>
                        </div>
                    );
                })}
            </div>

            {selectedHack && CREDIT_HACK_INFO[selectedHack] && (
                <div className="bg-[#111827] rounded-xl border border-zinc-800 p-5 cr-animate-slide" data-testid="hack-details">
                    <h4 className="text-sm font-semibold text-zinc-300 mb-3">Step-by-Step Instructions</h4>
                    <ol className="space-y-2 mb-4">
                        {CREDIT_HACK_INFO[selectedHack].instructions.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-zinc-400">
                                <span className="w-5 h-5 rounded-full bg-zinc-800 text-zinc-500 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                                {s}
                            </li>
                        ))}
                    </ol>
                    <Button size="sm" onClick={() => setShowDisputeForm(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="create-hack-dispute-btn">
                        <Plus className="w-3 h-3 mr-1" /> Create {CREDIT_HACK_INFO[selectedHack].title} Dispute
                    </Button>

                    {showDisputeForm && (
                        <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800 cr-animate-slide">
                            {error && <div className="text-red-400 text-xs mb-3">{error}</div>}
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <Label className="text-xs text-zinc-500">Bureau</Label>
                                    <select value={form.bureau} onChange={e => setForm(f => ({ ...f, bureau: e.target.value }))}
                                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300">
                                        {BUREAUS.map(b => <option key={b}>{b}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-xs text-zinc-500">Creditor/Company</Label>
                                    <Input value={form.creditor_name} onChange={e => setForm(f => ({ ...f, creditor_name: e.target.value }))}
                                        className="bg-zinc-900 border-zinc-700 text-zinc-300 h-9 text-sm" />
                                </div>
                                <div>
                                    <Label className="text-xs text-zinc-500">Amount ($)</Label>
                                    <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                                        className="bg-zinc-900 border-zinc-700 text-zinc-300 h-9 text-sm" />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs text-zinc-500">Reason (optional - AI will fill)</Label>
                                    <Textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                                        className="bg-zinc-900 border-zinc-700 text-zinc-300 text-sm min-h-12" />
                                </div>
                            </div>
                            <Button size="sm" onClick={createHackDispute} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                                <Sparkles className="w-3 h-3 mr-1" /> Create Dispute
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   EDUCATION TAB
   ═══════════════════════════════════════════════════════════════ */
function EducationTab({ modules }) {
    const categories = { fundamentals: 'Fundamentals', disputes: 'Disputes', negotiation: 'Negotiation', rebuilding: 'Rebuilding', mortgage: 'Mortgage Prep', financial: 'Financial' };
    return (
        <div className="space-y-5" data-testid="education-tab">
            <h3 className="text-lg font-semibold text-zinc-200 font-['Outfit']">Credit Education Center</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {modules.map((m, i) => (
                    <div key={m.id} className="bg-[#111827] rounded-xl border border-zinc-800 p-5 hover:border-zinc-700 transition-all cr-animate-slide"
                        style={{ animationDelay: `${i * 0.06}s` }}>
                        <div className="flex items-center justify-between mb-3">
                            <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-800 text-xs">{categories[m.category] || m.category}</Badge>
                            <span className="text-xs text-zinc-600">{m.duration}</span>
                        </div>
                        <h4 className="text-sm font-semibold text-zinc-200 mb-1">{m.title}</h4>
                        <p className="text-xs text-zinc-500 mb-3">{m.description}</p>
                        <div className="flex flex-wrap gap-1.5">
                            {m.topics.map((t, j) => (
                                <span key={j} className="text-xs bg-zinc-800 text-zinc-500 rounded-full px-2.5 py-0.5">{t}</span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   LEGAL ARSENAL TAB — 15 Letter Types
   ═══════════════════════════════════════════════════════════════ */
function LegalArsenalTab({ arsenal, onRefresh }) {
    const [selected, setSelected] = useState(null);
    const [genForm, setGenForm] = useState({ creditor_name: '', bureau: 'Experian', amount: '', extra_details: '', state: '' });
    const [generating, setGenerating] = useState(false);
    const [generatedLetter, setGeneratedLetter] = useState(null);

    if (!arsenal?.letters) return null;
    const letters = arsenal.letters;
    const categories = arsenal.categories;

    const CATEGORY_COLORS = {
        collector: { bg: 'bg-red-900/20', border: 'border-red-800', icon: 'text-red-400', badge: 'bg-red-900/50 text-red-400' },
        creditor: { bg: 'bg-amber-900/20', border: 'border-amber-800', icon: 'text-amber-400', badge: 'bg-amber-900/50 text-amber-400' },
        bureau: { bg: 'bg-blue-900/20', border: 'border-blue-800', icon: 'text-blue-400', badge: 'bg-blue-900/50 text-blue-400' },
        regulatory: { bg: 'bg-purple-900/20', border: 'border-purple-800', icon: 'text-purple-400', badge: 'bg-purple-900/50 text-purple-400' },
    };

    const generateLetter = async () => {
        if (!genForm.creditor_name.trim()) { toast.error('Enter creditor/company name'); return; }
        setGenerating(true);
        try {
            const { data } = await API.post('/credit-repair/legal-letter', {
                letter_type: selected, ...genForm, amount: parseFloat(genForm.amount || '0'),
            });
            setGeneratedLetter(data);
            toast.success(`${letters[selected].name} generated! Added to your disputes.`);
            onRefresh();
        } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
        finally { setGenerating(false); }
    };

    const groupedByCategory = {};
    Object.entries(letters).forEach(([key, val]) => {
        const cat = val.category || 'other';
        if (!groupedByCategory[cat]) groupedByCategory[cat] = [];
        groupedByCategory[cat].push({ key, ...val });
    });

    return (
        <div className="space-y-6" data-testid="legal-arsenal-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-zinc-200 font-['Outfit']">Legal Arsenal</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">15 legal weapons to fight inaccurate, unfair, or unverifiable items on your credit report.</p>
                </div>
                <Badge className="bg-emerald-900/50 text-emerald-400 border-emerald-800">{Object.keys(letters).length} letter types</Badge>
            </div>

            {Object.entries(groupedByCategory).map(([catKey, items]) => {
                const cc = CATEGORY_COLORS[catKey] || CATEGORY_COLORS.collector;
                return (
                    <div key={catKey} className="space-y-3">
                        <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">{categories[catKey]?.name || catKey}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {items.map((item, idx) => (
                                <div key={item.key} onClick={() => { setSelected(selected === item.key ? null : item.key); setGeneratedLetter(null); }}
                                    className={`rounded-xl border p-4 cursor-pointer transition-all duration-200 cr-animate-slide ${selected === item.key ? `${cc.bg} ${cc.border} ring-1 ring-${catKey === 'collector' ? 'red' : catKey === 'creditor' ? 'amber' : catKey === 'bureau' ? 'blue' : 'purple'}-500/30` : 'bg-[#111827] border-zinc-800 hover:border-zinc-700'}`}
                                    style={{ animationDelay: `${idx * 0.04}s` }}
                                    data-testid={`arsenal-card-${item.key}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-semibold text-zinc-200">{item.name}</span>
                                        <div className="flex items-center gap-1.5">
                                            {Array.from({ length: item.power_level }).map((_, i) => (
                                                <Zap key={i} className={`w-3 h-3 ${cc.icon}`} fill="currentColor" />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="text-xs text-zinc-500 mb-2">{item.description}</div>
                                    <div className="flex items-center justify-between">
                                        <Badge className={`${cc.badge} text-[10px]`}>{item.law}</Badge>
                                        <span className="text-[10px] text-zinc-600">Send to: {item.send_to}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}

            {/* Generate Form */}
            {selected && letters[selected] && (
                <div className="bg-[#111827] rounded-xl border border-zinc-800 p-5 cr-animate-slide" data-testid="arsenal-generate-form">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h4 className="text-sm font-semibold text-zinc-200">Generate: {letters[selected].name}</h4>
                            <p className="text-xs text-zinc-500 mt-0.5">{letters[selected].when_to_use}</p>
                        </div>
                        <button onClick={() => setSelected(null)} className="text-zinc-600 hover:text-zinc-400"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg p-3 mb-4 border border-zinc-800">
                        <div className="text-xs text-zinc-400"><strong>Deadline:</strong> {letters[selected].deadline}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                            <Label className="text-xs text-zinc-500">Creditor / Company</Label>
                            <Input value={genForm.creditor_name} onChange={e => setGenForm(f => ({ ...f, creditor_name: e.target.value }))}
                                placeholder="Capital One" className="bg-zinc-900 border-zinc-700 text-zinc-300 h-9 text-sm" data-testid="arsenal-creditor" />
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-500">Bureau</Label>
                            <select value={genForm.bureau} onChange={e => setGenForm(f => ({ ...f, bureau: e.target.value }))}
                                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300">
                                {BUREAUS.map(b => <option key={b}>{b}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-500">Amount ($)</Label>
                            <Input type="number" value={genForm.amount} onChange={e => setGenForm(f => ({ ...f, amount: e.target.value }))}
                                className="bg-zinc-900 border-zinc-700 text-zinc-300 h-9 text-sm" />
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-500">State (for SOL)</Label>
                            <Input value={genForm.state} onChange={e => setGenForm(f => ({ ...f, state: e.target.value }))}
                                placeholder="TX" maxLength={2} className="bg-zinc-900 border-zinc-700 text-zinc-300 h-9 text-sm" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs text-zinc-500">Additional Details (optional)</Label>
                            <Textarea value={genForm.extra_details} onChange={e => setGenForm(f => ({ ...f, extra_details: e.target.value }))}
                                placeholder="e.g., They call me 3 times a day, first contacted Jan 2024..."
                                className="bg-zinc-900 border-zinc-700 text-zinc-300 text-sm min-h-14" />
                        </div>
                    </div>
                    <Button onClick={generateLetter} disabled={generating}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg" data-testid="arsenal-generate-btn">
                        {generating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating AI Letter...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate {letters[selected].name}</>}
                    </Button>

                    {generatedLetter && (
                        <div className="mt-4 cr-animate-slide">
                            <div className="bg-emerald-900/20 border border-emerald-800 rounded-lg p-3 mb-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                                    <span className="text-sm text-emerald-300">Letter generated & added to disputes!</span>
                                </div>
                                <p className="text-xs text-emerald-400/60 mt-1">Send to: {generatedLetter.send_to} | Deadline: {generatedLetter.deadline}</p>
                            </div>
                            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 text-xs text-zinc-400 whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">
                                {generatedLetter.letter}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   SMART PLAN TAB — AI Analysis + Credit Optimization
   ═══════════════════════════════════════════════════════════════ */
function SmartPlanTab({ analysis, onRefresh }) {
    const [running, setRunning] = useState(false);
    const [localAnalysis, setLocalAnalysis] = useState(analysis);

    useEffect(() => { setLocalAnalysis(analysis); }, [analysis]);

    const runAnalysis = async () => {
        setRunning(true);
        try {
            const { data } = await API.post('/credit-repair/smart-analysis');
            setLocalAnalysis(data);
            toast.success(`Smart analysis complete! ${data.recommendations?.length || 0} recommendations generated.`);
            onRefresh();
        } catch (err) { toast.error('Analysis failed'); }
        finally { setRunning(false); }
    };

    const IMPACT_COLORS = {
        HIGH: 'bg-red-900/30 text-red-400 border-red-800',
        MEDIUM: 'bg-amber-900/30 text-amber-400 border-amber-800',
        LOW: 'bg-zinc-800 text-zinc-400 border-zinc-700',
    };

    return (
        <div className="space-y-6" data-testid="smart-plan-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-zinc-200 font-['Outfit']">Smart Credit Plan</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">AI analyzes your report and recommends the best legal tools + credit optimization strategies.</p>
                </div>
                <Button size="sm" onClick={runAnalysis} disabled={running}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg" data-testid="run-analysis-btn">
                    {running ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Analyzing...</> : <><Sparkles className="w-3 h-3 mr-1" /> Run AI Analysis</>}
                </Button>
            </div>

            {!localAnalysis ? (
                <div className="bg-[#111827] border border-zinc-800 rounded-xl p-10 text-center">
                    <Target className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm">Upload a credit report, then run Smart Analysis for personalized recommendations.</p>
                </div>
            ) : (
                <>
                    {/* Score Gap */}
                    <div className="bg-[#111827] border border-zinc-800 rounded-xl p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-zinc-300">Your Score Gap</h4>
                            <Badge className="bg-amber-900/50 text-amber-400">{localAnalysis.score?.gap || 0} points to go</Badge>
                        </div>
                        <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-all"
                                style={{ width: `${Math.min(100, Math.max(5, 100 - (localAnalysis.score?.gap || 0)))}%` }} />
                        </div>
                        <div className="flex justify-between text-xs text-zinc-500 mt-1.5">
                            <span>Current: ~{localAnalysis.score?.current_midpoint}</span>
                            <span>Target: {localAnalysis.score?.target}</span>
                        </div>
                    </div>

                    {/* AI Recommendations per item */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-zinc-300">Recommended Legal Strategy Per Item</h4>
                        {(localAnalysis.recommendations || []).map((r, i) => (
                            <div key={i} className={`rounded-xl border p-4 cr-animate-slide ${IMPACT_COLORS[r.estimated_impact] || IMPACT_COLORS.MEDIUM}`}
                                style={{ animationDelay: `${i * 0.05}s` }}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-zinc-200">{r.creditor_name}</span>
                                    <Badge className={IMPACT_COLORS[r.estimated_impact]}>{r.estimated_impact} Impact</Badge>
                                </div>
                                <p className="text-xs text-zinc-400 mb-2">{r.reasoning}</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {(r.recommended_tools || []).map((tool, j) => (
                                        <span key={j} className={`text-[10px] px-2.5 py-1 rounded-full border ${j === 0 ? 'bg-emerald-900/30 border-emerald-700 text-emerald-400 font-semibold' : 'bg-zinc-800 border-zinc-700 text-zinc-500'}`}>
                                            {j === 0 && <Star className="w-2.5 h-2.5 inline mr-0.5" />}
                                            {tool.replace(/_/g, ' ')}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Credit Optimization Strategies */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-zinc-300">Credit Score Optimization Playbook</h4>
                        {(localAnalysis.optimization || []).map((o, i) => (
                            <div key={o.strategy} className="bg-[#111827] border border-zinc-800 rounded-xl p-5 cr-animate-slide hover:border-zinc-700 transition-all"
                                style={{ animationDelay: `${i * 0.06}s` }} data-testid={`optimization-${o.strategy}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-sm font-semibold text-zinc-200">{o.name}</h5>
                                    <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700 text-[10px]">{o.impact?.split('—')[0]?.trim()}</Badge>
                                </div>
                                <p className="text-xs text-zinc-400 mb-2">{o.description}</p>
                                {o.personalized_note && (
                                    <div className="bg-emerald-900/20 border border-emerald-800/50 rounded-lg px-3 py-2 mb-3">
                                        <p className="text-xs text-emerald-400 flex items-center gap-1.5"><Sparkles className="w-3 h-3 shrink-0" /> {o.personalized_note}</p>
                                    </div>
                                )}
                                <ol className="space-y-1.5">
                                    {(o.steps || []).map((step, j) => (
                                        <li key={j} className="flex items-start gap-2 text-xs text-zinc-500">
                                            <span className="w-5 h-5 rounded-full bg-zinc-800 text-zinc-600 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{j + 1}</span>
                                            {step}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   TIMELINE TAB
   ═══════════════════════════════════════════════════════════════ */
function TimelineTab({ data }) {
    if (!data) return null;
    const { events, stats } = data;

    const EVENT_STYLES = {
        enrollment: { icon: Shield, color: 'text-emerald-400', bg: 'bg-emerald-900/30', line: 'border-emerald-600' },
        report_uploaded: { icon: Upload, color: 'text-blue-400', bg: 'bg-blue-900/30', line: 'border-blue-600' },
        report_comparison: { icon: TrendingUp, color: 'text-amber-400', bg: 'bg-amber-900/30', line: 'border-amber-600' },
        dispute_mailed: { icon: Mail, color: 'text-indigo-400', bg: 'bg-indigo-900/30', line: 'border-indigo-600' },
        item_resolved: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-900/30', line: 'border-emerald-600' },
        round_escalation: { icon: ArrowUp, color: 'text-red-400', bg: 'bg-red-900/30', line: 'border-red-600' },
        cfpb_complaint: { icon: AlertOctagon, color: 'text-amber-400', bg: 'bg-amber-900/30', line: 'border-amber-600' },
    };
    const fallback = { icon: Clock, color: 'text-zinc-400', bg: 'bg-zinc-800', line: 'border-zinc-700' };

    return (
        <div className="space-y-6" data-testid="timeline-tab">
            {/* Journey Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                    { label: 'Days Enrolled', value: stats?.days_enrolled || 0, icon: Calendar },
                    { label: 'Reports Uploaded', value: stats?.total_uploads || 0, icon: Upload },
                    { label: 'Disputes Filed', value: stats?.total_disputes || 0, icon: FileText },
                    { label: 'Letters Mailed', value: stats?.total_mailed || 0, icon: Mail },
                    { label: 'Items Resolved', value: stats?.total_resolved || 0, icon: CheckCircle },
                ].map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div key={s.label} className="bg-[#111827] border border-zinc-800 rounded-xl p-4 cr-animate-slide" style={{ animationDelay: `${i * 0.06}s` }}>
                            <Icon className="w-4 h-4 text-emerald-400 mb-1.5" />
                            <div className="text-xl font-bold text-white font-['Outfit']">{s.value}</div>
                            <div className="text-xs text-zinc-500">{s.label}</div>
                        </div>
                    );
                })}
            </div>

            {/* Timeline */}
            <div className="bg-[#111827] border border-zinc-800 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-zinc-300 mb-6">Your Credit Repair Journey</h3>
                <div className="relative">
                    {/* Vertical line */}
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-zinc-800" />

                    {events.length === 0 ? (
                        <p className="text-sm text-zinc-600 text-center py-8">Your journey starts here. Upload a credit report to begin!</p>
                    ) : (
                        <div className="space-y-4">
                            {events.map((e, i) => {
                                const style = EVENT_STYLES[e.event_type] || fallback;
                                const Icon = style.icon;
                                const dateStr = e.created_at ? new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                                return (
                                    <div key={e.id || i} className="relative flex gap-4 pl-1 cr-animate-slide" style={{ animationDelay: `${i * 0.04}s` }}>
                                        <div className={`relative z-10 w-8 h-8 rounded-full ${style.bg} border-2 ${style.line} flex items-center justify-center shrink-0`}>
                                            <Icon className={`w-3.5 h-3.5 ${style.color}`} />
                                        </div>
                                        <div className="flex-1 pb-4">
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm font-medium text-zinc-200">{e.title}</div>
                                                <span className="text-xs text-zinc-600 shrink-0">{dateStr}</span>
                                            </div>
                                            <p className="text-xs text-zinc-500 mt-0.5">{e.description}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
   AI CREDIT COACH — Floating Chat Widget
   ═══════════════════════════════════════════════════════════════ */
function AICreditCoach({ show, onToggle }) {
    const [messages, setMessages] = useState([
        { role: 'assistant', text: "Hey there! I'm your AI Credit Coach. I know your credit profile inside and out. Ask me anything — should you dispute or pay? What's the fastest way to hit your target score? I'm here to help. You've got this!" }
    ]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const sendMsg = async () => {
        if (!input.trim() || sending) return;
        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setSending(true);
        try {
            const { data } = await API.post('/credit-repair/coach', { message: userMsg, session_id: sessionId });
            setSessionId(data.session_id);
            setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
        } catch {
            setMessages(prev => [...prev, { role: 'assistant', text: "I'm having a moment — try again!" }]);
        }
        finally { setSending(false); }
    };

    const suggestions = [
        "Should I dispute or pay this collection?",
        "What's the fastest way to boost my score?",
        "How do I handle a debt validation letter?",
        "When should I apply for a mortgage?",
    ];

    return (
        <>
            {/* Floating toggle button */}
            <button onClick={onToggle}
                className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${show ? 'bg-zinc-800 rotate-0' : 'bg-gradient-to-br from-emerald-500 to-teal-600 cr-animate-pulse hover:shadow-emerald-500/30'}`}
                data-testid="coach-toggle-btn">
                {show ? <X className="w-5 h-5 text-zinc-300" /> : <Sparkles className="w-6 h-6 text-white" />}
            </button>

            {/* Chat panel */}
            {show && (
                <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-[#0f1520] border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden cr-animate-scale" data-testid="coach-panel">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-emerald-900 to-teal-900 px-5 py-3.5 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                            <Sparkles className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-white">AI Credit Coach</div>
                            <div className="text-[10px] text-emerald-300/60">Personalized to your credit profile</div>
                        </div>
                    </div>

                    {/* Messages */}
                    <div ref={scrollRef} className="h-72 overflow-y-auto px-4 py-3 space-y-3" data-testid="coach-messages">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} cr-animate-slide`}>
                                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${m.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-300'}`}>
                                    {m.text.split('\n').map((line, j) => <p key={j} className={j > 0 ? 'mt-1.5' : ''}>{line}</p>)}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="bg-zinc-800 rounded-xl px-4 py-3 flex gap-1">
                                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Quick Suggestions */}
                    {messages.length <= 1 && (
                        <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                            {suggestions.map((s, i) => (
                                <button key={i} onClick={() => { setInput(s); }}
                                    className="text-[11px] bg-zinc-800 text-zinc-400 rounded-full px-2.5 py-1 hover:bg-zinc-700 hover:text-zinc-300 transition-colors">
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <div className="px-3 pb-3 pt-1">
                        <div className="flex gap-2 bg-zinc-900 rounded-xl border border-zinc-800 px-3 py-1.5">
                            <input value={input} onChange={e => setInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && sendMsg()}
                                placeholder="Ask me anything about your credit..."
                                className="flex-1 bg-transparent text-sm text-zinc-300 outline-none placeholder-zinc-600"
                                data-testid="coach-input" />
                            <button onClick={sendMsg} disabled={sending || !input.trim()}
                                className="text-emerald-400 hover:text-emerald-300 disabled:text-zinc-700 transition-colors"
                                data-testid="coach-send-btn">
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}


/* ═══════════════════════════════════════════════════════════════
   ENROLLMENT VIEW
   ═══════════════════════════════════════════════════════════════ */
function EnrollmentView({ navigate }) {
    const plans = [
        { tier: 'Foundation', price: 'FREE', monthlyPrice: null, features: ['AI dispute letters', 'Breach scanner', '3 bureau tracking', 'Basic education'], popular: false, packageId: null },
        { tier: 'Accelerated', price: '$99', monthlyPrice: '$69/mo', features: ['Everything in Foundation', 'Credit Hacks engine', 'Priority AI analysis', '6-month program', 'Legal Arsenal access'], popular: true, packageId: 'credit_repair_setup' },
        { tier: 'Premium', price: '$99', monthlyPrice: '$69/mo', features: ['Everything in Accelerated', 'Direct lender connections', '4-month fast-track', 'VIP support', 'AI Credit Coach'], popular: false, packageId: 'credit_repair_setup' },
    ];
    return (
        <div className="min-h-screen bg-[#0a0f1a]" data-testid="enrollment-view">
            <Navbar variant="dark" />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="text-center mb-10 cr-animate-slide">
                    <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-3">Credit Repair Suite</h1>
                    <p className="text-zinc-400 text-base max-w-xl mx-auto">AI-powered credit repair with automated dispute generation, breach scanning, and a clear path to homeownership.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {plans.map((p, i) => (
                        <div key={p.tier} className={`rounded-2xl border p-6 cr-animate-slide ${p.popular ? 'border-emerald-600 bg-emerald-900/20 ring-1 ring-emerald-500/30' : 'border-zinc-800 bg-[#111827]'}`}
                            style={{ animationDelay: `${i * 0.1}s` }} data-testid={`plan-${p.tier.toLowerCase()}`}>
                            {p.popular && <Badge className="bg-emerald-600 text-white text-xs mb-3">Most Popular</Badge>}
                            <h3 className="text-lg font-semibold text-white font-['Outfit'] mb-1">{p.tier}</h3>
                            <div className="text-2xl font-bold text-emerald-400 mb-1 font-['Outfit']">{p.price}</div>
                            {p.monthlyPrice && <div className="text-xs text-zinc-500 mb-4">+ {p.monthlyPrice} after enrollment</div>}
                            {!p.monthlyPrice && <div className="text-xs text-zinc-500 mb-4">No credit card required</div>}
                            <ul className="space-y-2 mb-6">
                                {p.features.map((f, j) => (
                                    <li key={j} className="flex items-center gap-2 text-sm text-zinc-400">
                                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                            <Button onClick={() => navigate('/credit-repair/intake')} data-testid={`enroll-${p.tier.toLowerCase()}`}
                                className={`w-full rounded-xl ${p.popular ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-white'}`}>
                                Get Started <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
